import { ReactElement, useEffect, useState } from "react";
import { SampleSet } from "@/services/backend/sample-set/sample-set";
import { ProcedureType } from "@/services/backend/types";
import { useSwitchProcedureMutation } from "@/services/backend/procedures/procedure-switch/service";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import { Spinner } from "@/shared/components/ui/spinner";

interface SwitchProcedureModalProps {
  sampleSet: SampleSet;
  disabled?: boolean;
}

export default function SwitchProcedureModal({
  sampleSet,
  disabled,
}: SwitchProcedureModalProps): ReactElement {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [targetType, setTargetType] = useState<ProcedureType | undefined>(
    undefined,
  );

  const [switchProcedure, { error, isLoading, isSuccess, reset }] =
    useSwitchProcedureMutation();

  // Only procedures other than the current one are valid switch targets.
  const targetOptions = Object.values(ProcedureType).filter(
    (type) => type !== sampleSet.procedureType,
  );

  const canSubmit = targetType != null && reason.trim() !== "" && !isLoading;

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setReason("");
      setTargetType(undefined);
      reset();
    }
  };

  useEffect(() => {
    if (isSuccess) {
      setOpen(false);
      setReason("");
      setTargetType(undefined);
      reset();
    }
  }, [isSuccess, reset]);

  const onConfirm = () => {
    if (!canSubmit || targetType == null) {
      return;
    }
    switchProcedure({
      sampleSetId: sampleSet.id,
      toProcedureType: targetType,
      reason: reason.trim(),
      createdBy: user!.id,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          Verfahren wechseln
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Verfahren wechseln</DialogTitle>
          <DialogDescription>
            Das Probenset {sampleSet.sampleSetNumber} wird als Abweichung
            erhalten. Ein neues Set mit gleicher Nummer und nur den auswertbaren
            Proben wird mit dem gewählten Verfahren angelegt.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col space-y-4">
          <div className="flex w-full flex-col space-y-2">
            <Label>Neues Verfahren</Label>
            <Select
              value={targetType}
              onValueChange={(value: ProcedureType) => setTargetType(value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Bitte Verfahren wählen" />
              </SelectTrigger>
              <SelectContent>
                {targetOptions.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col space-y-2">
            <Label>Grund des Wechsels</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="z. B. Probe nicht auswertbar"
            />
          </div>

          {error && <RTKQueryErrorAlert error={error} />}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button disabled={!canSubmit} onClick={onConfirm}>
            {isLoading && <Spinner />}
            Wechsel bestätigen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
